import Phaser from 'phaser'

const toInt = (hex: string): number =>
  Phaser.Display.Color.HexStringToColor(hex).color

export type BoardTone =
  | 'message'
  | 'task'
  | 'done'
  | 'assign'
  | 'reaction'
  | 'insight'
  | 'note'

const TONE_COLOR: Record<BoardTone, string> = {
  message: '#6fb6ff',
  task: '#ef9f27',
  done: '#54d18c',
  assign: '#c9a0ff',
  reaction: '#ff7aa8',
  insight: '#ffd86b',
  note: '#7dbf8e',
}

const LINE_H = 13

/**
 * A wall-mounted screen showing a room's activity piling up — messages type in
 * and scroll, tasks land as chips. The content is abstract on purpose (no real
 * text): bars, dots, and status glyphs that read as "the team is working".
 */
export class RoomBoard {
  private readonly scene: Phaser.Scene
  private readonly x: number
  private readonly y: number
  private readonly w: number
  private readonly h: number
  private readonly maxLines: number
  private readonly lines: Phaser.GameObjects.Container[] = []

  constructor(scene: Phaser.Scene, x: number, y: number, w: number, h: number) {
    this.scene = scene
    this.x = x
    this.y = y
    this.w = w
    this.h = h
    this.maxLines = Math.max(2, Math.floor((h - 20) / LINE_H))
    this.draw()
  }

  private draw(): void {
    const frame = this.scene.add.graphics().setDepth(6)
    frame.fillStyle(toInt('#0a0c11'), 1)
    frame.lineStyle(1, toInt('#33384a'), 1)
    frame.fillRoundedRect(this.x, this.y, this.w, this.h, 5)
    frame.strokeRoundedRect(this.x, this.y, this.w, this.h, 5)
    frame.fillStyle(toInt('#11151d'), 1)
    frame.fillRect(this.x + 1, this.y + 1, this.w - 2, 13)
    this.scene.add
      .text(this.x + 7, this.y + 3, '▍ live feed', {
        fontFamily: 'monospace',
        fontSize: '9px',
        color: '#5fd39a',
      })
      .setDepth(7)
  }

  push(tone: BoardTone, actorColor: number, symbol?: string): void {
    const bottomY = this.y + this.h - 9
    for (const line of this.lines) {
      this.scene.tweens.add({
        targets: line,
        y: line.y - LINE_H,
        duration: 200,
        ease: 'Sine.out',
      })
    }

    const line = this.buildLine(tone, actorColor, symbol)
    line.setPosition(this.x + 8, bottomY)
    line.setDepth(7)
    line.setAlpha(0)
    this.scene.tweens.add({ targets: line, alpha: 1, duration: 180 })
    this.lines.push(line)

    while (this.lines.length > this.maxLines) {
      const old = this.lines.shift()
      if (!old) break
      this.scene.tweens.add({
        targets: old,
        alpha: 0,
        duration: 220,
        onComplete: () => old.destroy(),
      })
    }
  }

  private buildLine(
    tone: BoardTone,
    actorColor: number,
    symbol?: string,
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(0, 0)
    const color = TONE_COLOR[tone]
    const dot = this.scene.add.circle(2, 0, 2.5, actorColor)
    const maxW = Math.max(20, this.w - 44)
    const targetW =
      tone === 'message'
        ? Phaser.Math.Clamp(Phaser.Math.Between(40, 92), 20, maxW)
        : Math.min(26, maxW)
    // Tween scaleX, not width: a Shape's `width` is a plain size field that
    // does NOT re-render — tweening it left every bar a static 2px sliver.
    const bar = this.scene.add
      .rectangle(8, 0, targetW, 3, toInt(color))
      .setOrigin(0, 0.5)
      .setScale(2 / targetW, 1)
    this.scene.tweens.add({
      targets: bar,
      scaleX: 1,
      duration: tone === 'message' ? 480 : 200,
      ease: 'Linear',
    })
    container.add([dot, bar])
    if (symbol) {
      const glyph = this.scene.add.text(8 + targetW + 4, -6, symbol, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '10px',
        color,
      })
      container.add(glyph)
    }
    return container
  }
}
